import type { ResponseWithData, RouteGetParams, RouteParams} from '../helpers/ajax';
import {getJson, postJson, resolveRoute, responseToError} from '../helpers/ajax'
import type {SetStateAction} from 'react'
import {useStore} from '../helpers/react-form'
import {EMPTY_OBJECT} from '../helpers/constants'
import { useMutation, useQuery as _useQuery} from 'react-query'
import {useOnce} from './useOnce'
import type {UseMutationOptions, UseQueryOptions, UseQueryResult, QueryFunction, QueryKey, QueryObserverOptions, QueryOptions,MutationFunction} from 'react-query'
import {id} from '../kb'
import {queryClient} from '../helpers/query-client'
import type {JsonRequest, JsonResponse} from '../helpers/jsonSerializable'
import {resolveValue} from '../util/resolvable'
import {mergeOptions} from '../util/collections'

type SimpleQueryOptions<TData> = Omit<QueryOptions<TData>, 'queryKey' | 'queryFn' | '_defaulted'>
type SimpleObserverOptions<TData> = Omit<QueryObserverOptions<TData>, keyof QueryOptions>
type SimpleMutationOptions<TReqData, TResData, TError = unknown> = Omit<UseMutationOptions<TResData, TError, TReqData, unknown>, 'mutationFn' | 'mutationKey'>
// type SimpleUseQueryResult<TData,TError> = UseQueryResult<TData,TError>
// interface ExtendedUseQueryResult<TData,TError> extends UseQueryResult<TData,TError> {
//     invalidate(): void
// }

function useQuery<TQueryFnData = unknown, TError = unknown, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(queryKey: TQueryKey, queryFn: QueryFunction<TQueryFnData, TQueryKey>, options?: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
    // https://github.com/tannerlinsley/react-query/issues/1749
    const query = _useQuery(queryKey, queryFn, options)
    if (query.data === undefined && options?.placeholderData !== undefined) {
        // Fill `data` even on error
        query.data = resolveValue(options.placeholderData)
        query.isPlaceholderData = true
    }
    if (query.isSuccess && query.isFetching && query.isPlaceholderData) {
        // Set status to "loading" if real data hasn't been fetched yet
        query.isLoading = true
        query.isSuccess = false
        query.status = 'loading'
    }
    return query
}

type QueryHook<TResData, TReqData, TError = unknown> =
    (
        TReqData extends void|undefined|never
            ? (data?: undefined, observerOptions?: SimpleObserverOptions<TResData>) => UseQueryResult<TResData, TError>
            : (data: TReqData, observerOptions?: SimpleObserverOptions<TResData>) => UseQueryResult<TResData, TError>
    )
    & { invalidate(data?: TReqData): Promise<void> }

function createQuery<TResData, TReqData, TError = unknown>(queryFn: (req: TReqData) => Promise<TResData>, queryOptions?: SimpleQueryOptions<TResData>) {
    const key = id()
    const hook = (data: TReqData, observerOptions?: SimpleObserverOptions<TResData>) => useQuery(data !== undefined ? [key, data] : key, () => queryFn(data), mergeOptions(queryOptions,observerOptions))
    hook.invalidate = (data?: TReqData) => queryClient.invalidateQueries(data !== undefined ? [key, data] : key)
    return hook as QueryHook<TResData, TReqData, TError>
}

export function createMutation<TReqData extends JsonRequest|undefined,TResData extends JsonResponse|undefined,TError=unknown>(mutationFn: MutationFunction<TResData,TReqData>, defaultOptions?: SimpleMutationOptions<TReqData, TResData, TError>) {
    return (options?: SimpleMutationOptions<TReqData, TResData, TError>) => useMutation<TResData,TError,TReqData>(mutationFn,mergeOptions(defaultOptions,options))
}

type QueryError<T = unknown> = Error & { data: T, status: number }

function ajaxResponseHandler<T>(res: ResponseWithData<T>) {
    if (res.ok) return res.data
    const err: Partial<QueryError> = responseToError(res)
    err.data = res.data
    err.status = res.status
    throw err
}

export function createGetQuery<TResData extends JsonResponse, TReqData extends RouteGetParams|undefined = undefined, TErrorData = unknown>(route: string, queryOptions?: SimpleQueryOptions<TResData>) {
    return createQuery<TResData, TReqData, QueryError<TErrorData>>(data => getJson<TResData>(resolveRoute(route, data)).then(ajaxResponseHandler), queryOptions)
}

export function createPostQuery<TReqData extends JsonRequest, TResData extends JsonResponse, TErrorData = unknown>(route: RouteParams, queryOptions?: SimpleQueryOptions<TResData>) {
    return createQuery<TResData, TReqData, QueryError<TErrorData>>(data => postJson<TResData,TReqData>(resolveRoute(route),data).then(ajaxResponseHandler), queryOptions)
}

export function createPostMutation<TReqData extends JsonRequest|undefined, TResData extends JsonResponse|undefined=undefined, TErrorData = unknown>(route: string, options?: SimpleMutationOptions<TReqData, TResData, QueryError<TErrorData>>) {
    const mutationFn = (data: TReqData) => postJson<TResData, TReqData>(resolveRoute(route), data).then(ajaxResponseHandler)
    return createMutation<TReqData,TResData, QueryError<TErrorData>>(mutationFn, options)
}

export function createGetMutation<TReqData extends RouteGetParams|undefined, TResData extends JsonResponse|undefined=undefined, TErrorData = unknown>(route: string, options?: SimpleMutationOptions<TReqData, TResData, QueryError<TErrorData>>) {
    const mutationFn = (data: TReqData) => getJson<TResData>(resolveRoute(route, data)).then(ajaxResponseHandler)
    return createMutation<TReqData,TResData, QueryError<TErrorData>>(mutationFn, options)
}

/**
 * @deprecated Use `createGetQuery`
 */
export function useGetJson<TData>(route: RouteParams, options?: UseQueryOptions<TData>) {
    const url = resolveRoute(route)
    return useQuery<TData>(url, () => getJson<TData>(url).then(res => res.data), options)
}

/**
 * @deprecated Incorporate createGetQuery() somehow
 */
export function useLoadStore<TFormData, TServerData>(route: RouteParams, formBuilder: (data: TServerData) => SetStateAction<TFormData>)/*: LoadStoreResult<TFormData,TServerData>*/ {
    const store = useStore<TFormData>(EMPTY_OBJECT)
    const state = useGetJson<TServerData>(route)

    // alternatively, can use `onSuccess` option of react-query
    useOnce(() => {
        const formData = formBuilder(state.data)
        console.info("Transformed server data to form data", state.data, "->", formData)
        store.setData(formData)
    }, state.isSuccess)

    return {...state, store}
}
